import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  TMemberTeam,
  TMemberTeamChange,
  TTeam,
  TTeamCapacitySummary,
  TTeamCapacityTotals,
  TTeamMember,
  TTeamsListQuery,
  TTeamsPage,
  TCreateTeam,
  TDeleteTeamOptions,
  TTeamDeletePreflight,
  TTeamHomeRecordPicksBody,
  TTeamHomeRecordsPage,
  TTeamHomeRecordsQuery,
  TTeamHomeRecordsSummary,
  TUpdateTeam,
} from "@/types/team";
import type { TPartialProject } from "@/types/project";
import type { TWorkspaceCapacityGroupBy } from "@/types/workspace-capacity";
import type { components } from "@/types/api.generated";

/** What `teams/connected-users/` answers (NG-2967, NG-3175). */
export type TConnectedUsers = {
  userIds: string[];
  /** The connected people eligible to lead a project: who the Lead picker offers. */
  leadCandidateIds: string[];
};

type TCapacityRequestOptions = {
  includeMembers?: boolean;
  includeTotals?: boolean;
  memberPageSize?: number;
  memberCursor?: string | null;
  memberIds?: string[];
  requiredSkillIds?: string[];
};

type TCapacityDetailsRequestOptions = {
  memberIds?: string[];
};

/** Rows per teams-list page. The endpoint defaults to 100 and caps at 200; a
 *  tree page wants enough rows that a sub-team usually arrives with its parent,
 *  without paying for a 200-row payload on first paint. */
const TEAMS_PAGE_SIZE = 100;

function appendMemberIds(params: URLSearchParams, memberIds: string[] | undefined): void {
  if (memberIds?.length) params.set("member_ids", memberIds.join(","));
}

function appendCapacityPageOptions(
  params: URLSearchParams,
  options: TCapacityRequestOptions,
): void {
  if (options.includeTotals === false) params.set("include_totals", "false");
  if (typeof options.memberPageSize === "number")
    params.set("member_page_size", String(options.memberPageSize));
  if (options.memberCursor) params.set("member_cursor", options.memberCursor);
  appendMemberIds(params, options.memberIds);
  if (options.requiredSkillIds?.length)
    params.set("required_skill_ids", options.requiredSkillIds.join(","));
}

export class WorkspaceTeamService extends ApiService {
  async list(
    slug: string,
    options?: { includeAll?: boolean; includeMembers?: boolean },
    config?: ApiRequestConfig,
  ): Promise<TTeam[]> {
    const include = [
      ...(options?.includeAll ? ["all"] : []),
      ...(options?.includeMembers ? ["members"] : []),
    ].join(",");
    // Endpoint returns a cursor-paginated envelope now (issue #3234).
    // ``getAllPages`` walks the pages so this caller's contract stays
    // ``TTeam[]`` — matching every other workspace-list service.
    return this.getAllPages<TTeam>(
      ({ cursor, perPage }) => {
        const params = new URLSearchParams();
        if (include) params.set("include", include);
        params.set("per_page", String(perPage));
        if (cursor) params.set("cursor", cursor);
        return `/api/workspaces/${slug}/teams/?${params.toString()}`;
      },
      undefined,
      config,
    );
  }

  /**
   * One page of the teams list, keeping the server envelope (GH-3993). Distinct
   * from {@link list}, which walks every page and hands back a flat `TTeam[]`
   * for the sidebar / pickers: the teams *page* is server-driven — it searches,
   * orders and paginates in the query, so the caller needs `total_count` (the
   * FILTERED total) and the cursor to append the next page.
   *
   * `order=hierarchy` puts every parent ahead of its children across pages,
   * which is what lets the tree attach each appended page to already-loaded
   * parents. Any other `order` value is a 400 from the API — deliberately not
   * defaulted here so an invalid value surfaces instead of silently degrading.
   */
  async listPage(
    slug: string,
    options: TTeamsListQuery = {},
    config?: ApiRequestConfig,
  ): Promise<TTeamsPage> {
    const params = new URLSearchParams();
    const include = [
      ...(options.includeAll ? ["all"] : []),
      ...(options.includeMembers ? ["members"] : []),
    ].join(",");
    if (include) params.set("include", include);
    if (options.rootOnly) params.set("root_only", "true");
    // Blank/whitespace-only search is a no-op server-side; skip the param so the
    // request URL (and any cache keyed on it) stays identical to "no search".
    const search = options.search?.trim();
    if (search) params.set("search", search);
    if (options.order) params.set("order", options.order);
    if (options.mine) params.set("mine", "true");
    params.set("per_page", String(options.perPage ?? TEAMS_PAGE_SIZE));
    if (options.cursor) params.set("cursor", options.cursor);
    return this.get(`/api/workspaces/${slug}/teams/?${params.toString()}`, config);
  }

  async retrieve(slug: string, id: string, config?: ApiRequestConfig): Promise<TTeam> {
    return this.get(`/api/workspaces/${slug}/teams/${id}/`, config);
  }

  async listChildren(
    slug: string,
    id: string,
    mode: "direct" | "all" = "direct",
    config?: ApiRequestConfig,
  ): Promise<TTeam[]> {
    const res = await this.get<TTeam & { children: TTeam[] }>(
      `/api/workspaces/${slug}/teams/${id}/?children=${mode}`,
      config,
    );
    return res.children ?? [];
  }

  /**
   * The teams the caller may create under, for a given create verb.
   *
   * The default answers the team dialog's parent picker ("where may I create a
   * team?"); `"project.create"` answers the project dialog's delivery-team
   * picker ("which teams will the project create accept?", NG-2176). Both are
   * the same write question the respective create gate enforces, answered
   * server-side so a picker never re-derives the enforcer's acceptance policy
   * client-side (NG-1916 shape): a workspace-scope grant offers every team,
   * otherwise the offer is the grant teams and their subtrees. The backend
   * 400s an unlisted verb.
   */
  async listCreatable(
    slug: string,
    action: "team.create" | "project.create" = "team.create",
  ): Promise<TTeam[]> {
    const query = action === "team.create" ? "" : `?action=${action}`;
    return this.get(`/api/workspaces/${slug}/teams/creatable/${query}`);
  }

  /**
   * The teams *this caller* may put on a program's Team Access (NG-1916).
   *
   * Distinct from {@link list}: that returns every team the caller can SEE, which
   * is wider than what a program write accepts — the backend validates
   * `team_access` against `accessible_team_ids` (a lead or member of the team),
   * not workspace visibility. A non-admin who picked a merely-visible team saved
   * and got a 400/403; this endpoint answers the write question directly, so the
   * dropdown only ever offers a choice the save will accept. Admins get every
   * team, same as {@link listCreatable}.
   */
  async listAssignableForProgram(slug: string): Promise<TTeam[]> {
    return this.get(`/api/workspaces/${slug}/teams/assignable-for-program/`);
  }

  /**
   * The teams the caller may make a project's or program's Home (NG-2969): the
   * teams they are directly on, never the teams below them, which
   * {@link listAssignableForProgram} adds. Admins get every team. The write
   * enforces it.
   */
  async listHomeEligible(slug: string): Promise<TTeam[]> {
    return this.get(`/api/workspaces/${slug}/teams/home-eligible/`);
  }

  /**
   * Who `teamIds` connect (NG-2967, spec 2.1 and 2.2): the members of those teams
   * and of every team above them, and which of them may lead a project (NG-3175).
   * The Lead picker reads it for the roster it is showing. Teams the caller cannot see count for nothing.
   * An API from before `lead_candidate_ids` offers everyone connected, as it did.
   */
  async listConnectedUsers(slug: string, teamIds: readonly string[]): Promise<TConnectedUsers> {
    const data = await this.get<Partial<components["schemas"]["TeamConnectedUsers"]>>(
      `/api/workspaces/${slug}/teams/connected-users/`,
      {
        params: { teams: teamIds.join(",") },
      },
    );
    const userIds = data?.user_ids ?? [];
    return { userIds, leadCandidateIds: data?.lead_candidate_ids ?? userIds };
  }

  /**
   * Who a new program delivered by `teamIds` may be owned by (NG-3164, spec 5.1):
   * people whose role can own a program and who are on one of those teams or a team
   * above one. The create POST accepts exactly these.
   * `programId` asks about a change to that program's teams, whose current teams
   * count as kept, as its PATCH counts them.
   */
  async listProgramOwnerCandidates(
    slug: string,
    teamIds: readonly string[],
    programId?: string,
  ): Promise<string[] | null> {
    const params = new URLSearchParams();
    for (const teamId of teamIds) params.append("team_access", teamId);
    if (programId) params.append("program", programId);
    const data = await this.get<{ candidate_ids: string[] | null }>(
      `/api/workspaces/${slug}/programs/owner-candidates/`,
      { params },
    );
    return data?.candidate_ids ?? null;
  }

  async getCapacity(
    slug: string,
    id: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    options: TCapacityRequestOptions = { includeMembers: true },
    config?: ApiRequestConfig,
  ): Promise<TTeamCapacitySummary> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });
    if (options.includeMembers ?? true) params.set("include_members", "true");
    appendCapacityPageOptions(params, options);
    return this.get(
      `/api/workspaces/${slug}/teams/${id}/capacity-allocations/?${params.toString()}`,
      config,
    );
  }

  async getCapacityTotals(
    slug: string,
    id: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    config?: ApiRequestConfig,
  ): Promise<TTeamCapacityTotals> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });
    return this.get(
      `/api/workspaces/${slug}/teams/${id}/capacity-allocations/totals/?${params.toString()}`,
      config,
    );
  }

  async getCapacityDetails(
    slug: string,
    id: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    options: TCapacityDetailsRequestOptions = {},
  ): Promise<TTeamCapacitySummary> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });
    appendMemberIds(params, options.memberIds);
    return this.get(
      `/api/workspaces/${slug}/teams/${id}/capacity-allocations/details/?${params.toString()}`,
    );
  }

  async create(slug: string, data: TCreateTeam): Promise<TTeam> {
    return this.post(`/api/workspaces/${slug}/teams/`, data);
  }

  async update(slug: string, id: string, data: TUpdateTeam): Promise<TTeam> {
    return this.patch(`/api/workspaces/${slug}/teams/${id}/`, data);
  }

  async remove(slug: string, id: string, options: TDeleteTeamOptions = {}): Promise<void> {
    // No options sends the bare DELETE, exactly as before NG-2157.
    const params: Record<string, string> = {};
    if (options.rehome) params.rehome = options.rehome;
    if (options.promoteSubteams) params.promote_subteams = "1";
    return this.delete(
      `/api/workspaces/${slug}/teams/${id}/`,
      Object.keys(params).length ? { params } : undefined,
    );
  }

  async deletePreflight(slug: string, id: string): Promise<TTeamDeletePreflight> {
    return this.get(`/api/workspaces/${slug}/teams/${id}/delete-preflight/`);
  }

  /** One page of a root team's live Home records, with each one's pick (NG-2156). */
  async listHomeRecords(
    slug: string,
    id: string,
    query: TTeamHomeRecordsQuery = {},
    config?: ApiRequestConfig,
  ): Promise<TTeamHomeRecordsPage> {
    const params = new URLSearchParams();
    if (query.kind) params.set("kind", query.kind);
    const q = query.q?.trim();
    if (q) params.set("q", q);
    if (query.needsTeam) params.set("needs_team", "1");
    if (query.perPage) params.set("per_page", String(query.perPage));
    if (query.cursor) params.set("cursor", query.cursor);
    const search = params.toString();
    return this.get(
      `/api/workspaces/${slug}/teams/${id}/home-records/${search ? `?${search}` : ""}`,
      config,
    );
  }

  async homeRecordsSummary(
    slug: string,
    id: string,
    config?: ApiRequestConfig,
  ): Promise<TTeamHomeRecordsSummary> {
    return this.get(`/api/workspaces/${slug}/teams/${id}/home-records/summary/`, config);
  }

  /** Save picks (one record, a selection, or all); answers with the new summary. */
  async saveHomeRecordPicks(
    slug: string,
    id: string,
    body: TTeamHomeRecordPicksBody,
  ): Promise<TTeamHomeRecordsSummary> {
    return this.put(`/api/workspaces/${slug}/teams/${id}/home-records/picks/`, body);
  }

  /** The whole list as CSV, for the editor's "Download list (CSV)". */
  async exportHomeRecords(slug: string, id: string): Promise<Blob> {
    return this.get(`/api/workspaces/${slug}/teams/${id}/home-records/export/`, {
      responseType: "blob",
    });
  }

  /** Raw, so the caller can branch an inline 200 from a queued 202 (`runBulkAction`). */
  async reassignAndDelete(slug: string, id: string): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/teams/${id}/reassign-and-delete/`, {});
  }

  async listMembers(slug: string, id: string, config?: ApiRequestConfig): Promise<TTeamMember[]> {
    return this.get(`/api/workspaces/${slug}/teams/${id}/members/`, config);
  }

  async setMembers(slug: string, id: string, memberIds: string[]): Promise<TTeamMember[]> {
    return this.post(`/api/workspaces/${slug}/teams/${id}/members/`, {
      member_ids: memberIds,
    });
  }

  /** The teams *one member* is on, with their role on each (NG-837). The
   *  inverse of {@link listMembers}; includes teams they lead. */
  async listMemberTeams(
    slug: string,
    memberId: string,
    config?: ApiRequestConfig,
  ): Promise<TMemberTeam[]> {
    return this.get(`/api/workspaces/${slug}/members/${memberId}/teams/`, config);
  }

  /** Change *memberId*'s role on the named teams only (`role: null` leaves a
   *  team), answered with every team they are on afterwards. Teams not named
   *  are untouched, so a change made elsewhere since the list was read can
   *  neither fail this write nor be undone by it (NG-2566). */
  async changeMemberTeams(
    slug: string,
    memberId: string,
    teams: TMemberTeamChange[],
  ): Promise<TMemberTeam[]> {
    return this.patch(`/api/workspaces/${slug}/members/${memberId}/teams/`, { teams });
  }

  async removeMember(slug: string, teamId: string, teamMemberId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/teams/${teamId}/members/${teamMemberId}/`);
  }

  async listTeamProjects(
    slug: string,
    teamId: string,
    filters?: { intake_submission?: string },
  ): Promise<TPartialProject[]> {
    // Server-paginated; walk the pages so the team projects table keeps the
    // full linked-project set it filters/sorts locally.
    return this.getAllPages<TPartialProject>(({ cursor, perPage }) => {
      const qs = new URLSearchParams({ per_page: String(perPage) });
      if (filters?.intake_submission) qs.set("intake_submission", filters.intake_submission);
      if (cursor) qs.set("cursor", cursor);
      return `/api/workspaces/${slug}/teams/${teamId}/projects/?${qs}`;
    });
  }
}
